import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppConfig } from '@app/core/app.config';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { ClientInput } from '@app/core/model/client/client';
import { Module } from '@app/core/model/client/module';
import { Organization } from '@app/core/model/entities/organization/organization';
import { provideDayjsDateAdapter } from '@app/shared/extra/dayjs-date-adapter';
import { getLocale } from '@app/shared/extra/utils';
import { AbstractModalDialog } from '@app/shared/interfaces/abstract-modal-dialog';
import { GeneratesObject } from '@app/shared/interfaces/generates-object';
import { OrganizationsService } from '@app/shared/services/organizations.service';
import { ClientNameReset } from '@app/shared/store/reducers/client-name.actions';
import { ExtraValidators } from '@app/shared/validators/extra-validators.module';
import { Store } from '@ngrx/store';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import dayjs, { Dayjs } from 'dayjs';
import { debounceTime } from 'rxjs/operators';

type SubscriptionModulesForm = FormGroup<{
  moduleId: FormControl<number>
  active: FormControl<boolean>,
  name: FormControl<string>,
  nbLicences: FormControl<number>,
  limitedDuration: FormControl<boolean>,
  isDefault: FormControl<boolean>
}>;

@Component({
  templateUrl: './client-create-modal.component.html',
  styleUrls: ['./client-create-modal.component.scss'],
  providers: [provideDayjsDateAdapter()],
  standalone: false
})
export class ClientCreateModalComponent extends AbstractModalDialog implements OnInit, OnDestroy, GeneratesObject {
  protected appConfig = inject(AppConfig);
  protected appManager = inject(AppManager);
  protected data = inject(MAT_DIALOG_DATA);
  private apiService = inject(ApiService);
  private organizationsService = inject(OrganizationsService);
  private store = inject<Store<any>>(Store);
  private fb = inject(FormBuilder);

  public clientNameForm: FormGroup<{ clientName: FormControl<string>, organization?: FormControl<string> }>;

  public clientSubscriptionsForm: FormGroup<{
    subscriptionStart: FormControl<Dayjs>,
    subscriptionDuration: FormControl<number>,
    subscriptionNbAuthorizedAccounts: FormControl<number>,
    subscriptionContractId: FormControl<string>,
    subscriptionModules: FormArray<SubscriptionModulesForm>,
  }>;

  public organizationsList: Organization[];
  public title = 'TITLE.CREATE_CLIENT';
  public minStartDate = dayjs();
  private createQuery: boolean;

  constructor() {
    super();

    this.createQuery = false;
    this.hideSpinner = (): boolean => this.createQuery;

    // Load organizations list
    this.organizationsService.loadData({
      sorts: [{field: 'name', direction: 'ASC'}],
      locale: getLocale()
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => this.organizationsList = response);
  }

  public ngOnInit(): void {
    /*
    Form to edit the client name
     */
    this.clientNameForm = this.fb.group({
      clientName: this.fb.control(
        '',
        Validators.compose([Validators.required, Validators.maxLength(this.appConfig.NAME_MAX_LENGTH)]),
        ExtraValidators.isValueTaken(this.apiService, EntityTypeEnum.CLIENT, 'name', '', {}, {
          store: this.store,
          fieldName: 'clientName'
        })
      )
    });
    if (this.appManager.currentOrganization == null) {
      this.clientNameForm.addControl('organization', this.fb.control('', Validators.compose([Validators.required])));
    }

    /*
    Form to edit the client subscription
     */
    this.clientSubscriptionsForm = this.fb.group({
      subscriptionStart: this.fb.control(null, Validators.required),
      subscriptionDuration: this.fb.control(null, Validators.compose([
        Validators.required,
        ExtraValidators.number,
        ExtraValidators.gte(1)
      ])),
      subscriptionNbAuthorizedAccounts: this.fb.control(null, Validators.compose([
        Validators.required,
        ExtraValidators.number,
        ExtraValidators.gte(1)
      ])),
      subscriptionContractId: this.fb.control(null, Validators.maxLength(this.appConfig.CONTRACT_ID_MAX_LENGTH)),
      subscriptionModules: this.fb.array<SubscriptionModulesForm>([])
    });

    /*
    Request to get all modules
     */
    const query = gql`
      query ModuleList {
        modules(onlySubscribable: true) {
          id
          code
          order
          isDefault
        }
      }
    `;
    this.apiService.query({query})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.createQuery = true;
        const modules = plainToInstance(Module, data['modules'] as Module[]);

        this.initialiseForm(modules);
      });
  }

  public ngOnDestroy(): void {
    this.store.dispatch(new ClientNameReset({currentValue: '', originalValue: ''}));
  }

  private initialiseForm(modules: Module[]): void {
    const moduleFormArray = this.fb.array<SubscriptionModulesForm>(modules.sort((moduleA, moduleB) =>
      moduleA.order.compareTo(moduleB.order)
    ).map((module: Module) => {
      const moduleItem = this.fb.group({
        moduleId: this.fb.control(module.id),
        active: this.fb.control({value: module.isDefault, disabled: module.isDefault}),
        name: this.fb.control(module.code),
        nbLicences: this.fb.control(
          {value: null, disabled: true},
          Validators.compose([Validators.required, ExtraValidators.number])
        ),
        limitedDuration: this.fb.control({value: false, disabled: true}),
        isDefault: this.fb.control(module.isDefault)
      });

      // Change detection for the module checkboxes
      moduleItem.controls.active.valueChanges.pipe(
        debounceTime(50),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((value) => {
        if (value) {
          // Enable validation on the number of licences field
          moduleItem.controls.nbLicences.enable();
          moduleItem.controls.nbLicences.markAsTouched();

          // If number of licences was not previously set, default to 1
          if (moduleItem.controls.nbLicences.value === '') {
            moduleItem.controls.nbLicences.setValue(1);
          }

        } else {
          // Disable validation
          moduleItem.controls.nbLicences.disable();

          // Reset fields
          moduleItem.controls.nbLicences.reset('');
          moduleItem.controls.limitedDuration.reset(false);
        }
      });

      return moduleItem;
    }));

    this.clientSubscriptionsForm.setControl('subscriptionModules', moduleFormArray);
  }

  /**
   * Parse the form's data to extract data for creating a Client and its initial subscription.
   * @returns Input data for a Client and initial Subscription.
   */
  public getGeneratedObject(): { organizationId: string, client: ClientInput } {
    const startDate = this.clientSubscriptionsForm.value.subscriptionStart.startOf('day');
    const endDate = startDate.add(this.clientSubscriptionsForm.value.subscriptionDuration, 'month');

    return {
      organizationId: this.appManager.currentOrganization
        ? this.appManager.currentOrganization.id
        : this.clientNameForm.controls.organization.value,
      client: {
        name: this.clientNameForm.controls.clientName.value,
        subscription: {
          startDate: startDate.format(),
          endDate: endDate.format(),
          nbUser: this.clientSubscriptionsForm.value.subscriptionNbAuthorizedAccounts,
          contractId: this.clientSubscriptionsForm.value.subscriptionContractId,
          subscriptionModules: this.clientSubscriptionsForm.controls.subscriptionModules.controls
            .filter(moduleFormGroup => moduleFormGroup.controls.active.value)
            .map(moduleFormGroup => {
              const moduleEndDate = moduleFormGroup.value.limitedDuration ? startDate.add(1, 'month') : endDate;
              return {
                startDate: startDate.format(),
                endDate: moduleEndDate.format(),
                limitedDuration: moduleFormGroup.controls.limitedDuration.value,
                nbLicence: moduleFormGroup.controls.isDefault.value
                  ? this.clientSubscriptionsForm.controls.subscriptionNbAuthorizedAccounts.value
                  : moduleFormGroup.controls.nbLicences.value,
                moduleId: moduleFormGroup.value.moduleId,
                userIds: []
              };
            })
        }
      }
    };
  }

}
